import React, { useCallback, useEffect, useMemo } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { Button } from "@coval/ui"
import { Info, Library } from "lucide-react"

import { EntitySelectionCard } from "@/components/entity-picker/EntitySelectionCard"
import { useEntityPickerDraft } from "@/components/entity-picker/useEntityPickerDraft"
import type { Persona } from "@/types/api/Persona"

import { usePersonasList } from "../hooks/usePersonas"
import { usePersonaCatalogSelectionTransport } from "../routes/usePersonaCatalogSelectionTransport"
import { AddPersonaModal } from "./AddPersonaModal"
import { PersonaPickerDialog } from "./PersonaPickerDialog"

const PersonaIcon = ENTITY_ICONS.personas

interface PersonaFeatureProps {
  selectedPersonaIds: string[]
  onPersonaChange: (personaIds: string[]) => void
  personas?: Persona[]
  disabled?: boolean
  readOnly?: boolean
  singleSelect?: boolean
  tagColorMap?: Record<string, string | null | undefined>
  showMultiRunWarning?: boolean
  /**
   * Optional action offered inside the persona picker, for a caller that can
   * create one, so a user with no personas is not stuck at an empty catalog.
   */
  footer?: React.ReactNode
  allowCatalogAddition?: boolean
}

export const PersonaSelector: React.FC<PersonaFeatureProps> = ({
  selectedPersonaIds,
  onPersonaChange,
  personas: propPersonas,
  disabled = false,
  readOnly = false,
  singleSelect = false,
  tagColorMap,
  showMultiRunWarning = true,
  footer,
  allowCatalogAddition = false,
}) => {
  const {
    data: loadedPersonas,
    isLoading,
    isError,
  } = usePersonasList({ enabled: !propPersonas })

  const isDisabled = disabled || readOnly
  const hasMultiplePersonas = selectedPersonaIds.length > 1

  const picker = useEntityPickerDraft(selectedPersonaIds, onPersonaChange, {
    disabled: isDisabled,
  })

  const catalog = usePersonaCatalogSelectionTransport({
    enabled: allowCatalogAddition && !isDisabled,
    pickerOpen: picker.isOpen,
    singleSelect,
    setDraftIds: picker.setDraftIds,
    cancelPicker: picker.cancel,
  })
  const personas = useMemo(() => {
    const items = propPersonas ?? loadedPersonas ?? []
    const ids = new Set(items.map((persona) => persona.id))
    return [
      ...items,
      ...catalog.copiedPersonas.filter((persona) => !ids.has(persona.id)),
    ]
  }, [catalog.copiedPersonas, loadedPersonas, propPersonas])

  const cancelPicker = picker.cancel
  useEffect(() => {
    if (isDisabled) {
      cancelPicker()
    }
  }, [cancelPicker, isDisabled])

  // Chips follow the committed selection order, and an id with no matching
  // record still gets one so it can be removed.
  const chips = useMemo(
    () =>
      selectedPersonaIds.map((id) => {
        const persona = personas.find((p) => p.id === id)
        return {
          id,
          label: persona?.display_name || id,
          isMissing: !persona && !isLoading,
        }
      }),
    [isLoading, personas, selectedPersonaIds],
  )

  const handleRemovePersona = useCallback(
    (personaId: string) => {
      onPersonaChange(selectedPersonaIds.filter((id) => id !== personaId))
    },
    [onPersonaChange, selectedPersonaIds],
  )

  return (
    <>
      <EntitySelectionCard
        icon={PersonaIcon}
        title="Persona"
        chips={chips}
        onRemove={handleRemovePersona}
        onBrowse={picker.open}
        onClearAll={() => onPersonaChange([])}
        browseLabel="Browse Personas"
        addLabel="Add Personas"
        emptyMessage={isError ? "Failed to load personas" : undefined}
        browseTestId="persona-browse-button"
        disabled={isDisabled}
      >
        {showMultiRunWarning && hasMultiplePersonas && (
          <div className="p-4 rounded-lg bg-coval-warning-surface border border-coval-warning-border">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-coval-warning-on-surface mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-coval-warning-on-surface">
                  Multiple Personas Selected
                </p>
                <p className="text-sm text-coval-warning-on-surface mt-1">
                  You have selected multiple personas. When you launch, we will
                  run multiple jobs—one for each persona selected. Note:
                  templates only support a single persona and cannot be used
                  with multiple selections.
                </p>
              </div>
            </div>
          </div>
        )}
      </EntitySelectionCard>
      <PersonaPickerDialog
        isOpen={picker.isOpen}
        onCancel={() => {
          catalog.closeCatalog()
          picker.cancel()
        }}
        onApply={picker.apply}
        personas={personas}
        isLoading={isLoading}
        selectedIds={picker.draftIds}
        onSelectionChange={picker.setDraftIds}
        allowMultiple={!singleSelect}
        tagColorMap={tagColorMap}
        createAction={
          <div className="flex flex-wrap items-center gap-2">
            {allowCatalogAddition && (
              <Button
                variant="secondary"
                onClick={catalog.openCatalog}
                disabled={!catalog.canOpenCatalog}
                data-testid="persona-add-from-catalog-button"
              >
                <Library className="size-4" />
                Add from Catalog
              </Button>
            )}
            {footer}
          </div>
        }
        catalogPanel={
          catalog.catalogOpen && (
            <AddPersonaModal
              isOpen
              onClose={catalog.closeCatalog}
              onAdd={catalog.addCatalogPersona}
              presentation="picker-panel"
              addLabel="Add & Select"
              doneLabel="Back to Selection"
            />
          )
        }
      />
    </>
  )
}
